import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export const useTemplateForms = () => {
  const [forms, setForms] = useState([]);
  useEffect(() => { axios.get(`${API}/email-templates-meta/forms`, auth()).then((r) => setForms(r.data)).catch(() => {}); }, []);
  return forms;
};

// How a custom template is used: automatically on site forms, or sent by hand
export const TemplateUsage = ({ formData, onChange, forms }) => {
  const picked = formData.trigger_forms || [];
  const toggle = (k) => onChange("trigger_forms", picked.includes(k) ? picked.filter((x) => x !== k) : [...picked, k]);
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="email-template-usage">
      <Label>How is this template used?</Label>
      <div className="flex flex-wrap gap-4 text-sm">
        {[["form", "Sent automatically when a form is submitted"], ["manual", "Sent manually to one person"]].map(([v, l]) => (
          <label key={v} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={formData.usage === v} onChange={() => onChange("usage", v)} data-testid={`email-usage-${v}`} />{l}
          </label>
        ))}
      </div>
      {formData.usage === "form" && (
        <>
          <div className="grid sm:grid-cols-2 gap-2">
            {forms.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={picked.includes(f.key)} onChange={() => toggle(f.key)} data-testid={`email-trigger-${f.key}`} />{f.label}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {[["submitter", "Send to the person who filled in the form"], ["staff", "Send to our team"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={(formData.recipient || "submitter") === v} onChange={() => onChange("recipient", v)} data-testid={`email-recipient-${v}`} />{l}
              </label>
            ))}
          </div>
          {formData.recipient === "staff" && (
            <Input value={formData.staff_emails || ""} onChange={(e) => onChange("staff_emails", e.target.value)} placeholder="info@primemediawireless.com, sam@primemediawireless.com" data-testid="email-staff-emails" />
          )}
        </>
      )}
    </div>
  );
};

export const SendTemplateDialog = ({ slug, open, onClose }) => {
  const [v, setV] = useState({ to: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/email-templates/${slug}/send`, { to: v.to, variables: { first_name: v.first_name, last_name: v.last_name } }, auth());
      toast.success(r.data.message); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="email-send-dialog">
        <DialogHeader><DialogTitle>Send this email</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>To</Label><Input type="email" value={v.to} onChange={(e) => setV({ ...v, to: e.target.value })} data-testid="email-send-to" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First name</Label><Input value={v.first_name} onChange={(e) => setV({ ...v, first_name: e.target.value })} data-testid="email-send-first" /></div>
            <div><Label>Last name</Label><Input value={v.last_name} onChange={(e) => setV({ ...v, last_name: e.target.value })} data-testid="email-send-last" /></div>
          </div>
          <Button onClick={send} disabled={busy || !v.to} className="bg-teal hover:bg-teal-dark text-white w-full" data-testid="email-send-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BLANK = { name: "", usage: "form", trigger_forms: [], recipient: "submitter", subject: "", body_html: "<p>Hi {{first_name}},</p>" };

export const NewTemplateDialog = ({ open, onClose, onCreated, forms }) => {
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (open) setF(BLANK); }, [open]);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/email-templates`, f, auth()); toast.success("Template created"); onCreated(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create template"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="email-new-dialog">
        <DialogHeader><DialogTitle>New email template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Template name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Careers follow-up" data-testid="email-new-name" /></div>
          <div><Label>Subject line</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Thanks, {{first_name}}!" data-testid="email-new-subject" /></div>
          <TemplateUsage formData={f} forms={forms} onChange={(k, val) => setF((p) => ({ ...p, [k]: val }))} />
          <Button onClick={create} disabled={busy || !f.name} className="bg-teal hover:bg-teal-dark text-white w-full" data-testid="email-new-create">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
